import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { TrendingDown, TrendingUp, Package, DollarSign, Calculator, Clock, Upload, Building2 } from "lucide-react";

const CHART_COLORS = ["#0B1020", "#C8A961", "#065F46", "#1E40AF", "#92400E", "#991B1B", "#4A5568", "#2D3748", "#718096", "#A0AEC0", "#E8E2D5", "#F8F5EF", "#1A233A", "#6B7280"];

function KpiCard({ label, value, icon: Icon, trend, delay = 0 }) {
  return (
    <div className={`stratum-card p-5 animate-fade-in-up`} style={{ animationDelay: `${delay}ms` }} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs text-[#6B7280] uppercase tracking-wider font-medium">{label}</span>
        <div className="w-8 h-8 rounded-lg bg-[#FAF8F4] flex items-center justify-center">
          <Icon size={16} className="text-[#C8A961]" strokeWidth={1.5} />
        </div>
      </div>
      <div className="font-mono text-2xl font-semibold text-[#0B1020] tabular-nums">{value}</div>
      {trend && <div className={`text-xs mt-2 ${trend > 0 ? "text-[#065F46]" : "text-[#991B1B]"}`}>{trend > 0 ? "+" : ""}{trend}% vs prior</div>}
    </div>
  );
}

export default function DashboardPage() {
  const { activeEntity, getCategoryName } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = activeEntity ? { entity_id: activeEntity.id } : {};
        const { data: d } = await api.get("/dashboard", { params });
        setData(d);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeEntity]);

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="stratum-card p-5 animate-pulse">
              <div className="h-4 bg-[#E8E2D5] rounded w-24 mb-3" />
              <div className="h-8 bg-[#E8E2D5] rounded w-32" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state - onboarding
  if (data.asset_count === 0) {
    return (
      <div className="space-y-6" data-testid="dashboard-page">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">Welcome to Stratum</h1>
          <p className="text-sm text-[#6B7280] mt-1">Let's set up your asset register</p>
        </div>

        <div className="max-w-3xl">
          <div className="stratum-card p-8 text-center mb-6">
            <img src="/assets/stratum_logomark.png" alt="Stratum" className="w-20 h-20 mx-auto mb-4 rounded-2xl" />
            <h2 className="font-display text-xl font-semibold text-[#0B1020] mb-2">The Intelligent Asset Register</h2>
            <p className="text-[#6B7280] max-w-lg mx-auto">Get started by importing your existing asset register or adding your first entity and assets manually.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="/import" className="stratum-card p-6 hover:shadow-md transition-shadow text-center group" data-testid="onboard-import">
              <div className="w-12 h-12 rounded-xl bg-[#C8A961]/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-[#C8A961]/20 transition-colors">
                <Upload size={24} className="text-[#C8A961]" />
              </div>
              <h3 className="font-display text-base font-semibold text-[#0B1020] mb-1">Import from CSV</h3>
              <p className="text-xs text-[#6B7280]">Upload your existing FAR. Entities and assets auto-created from your data.</p>
            </a>

            <a href="/settings" className="stratum-card p-6 hover:shadow-md transition-shadow text-center group" data-testid="onboard-entities">
              <div className="w-12 h-12 rounded-xl bg-[#0B1020]/5 flex items-center justify-center mx-auto mb-3 group-hover:bg-[#0B1020]/10 transition-colors">
                <Building2 size={24} className="text-[#0B1020]" />
              </div>
              <h3 className="font-display text-base font-semibold text-[#0B1020] mb-1">Set Up Entities</h3>
              <p className="text-xs text-[#6B7280]">Add your group companies manually and configure their details.</p>
            </a>

            <a href="/assets" className="stratum-card p-6 hover:shadow-md transition-shadow text-center group" data-testid="onboard-assets">
              <div className="w-12 h-12 rounded-xl bg-[#065F46]/5 flex items-center justify-center mx-auto mb-3 group-hover:bg-[#065F46]/10 transition-colors">
                <Package size={24} className="text-[#065F46]" />
              </div>
              <h3 className="font-display text-base font-semibold text-[#0B1020] mb-1">Add Assets</h3>
              <p className="text-xs text-[#6B7280]">Manually add assets one by one with full depreciation policy control.</p>
            </a>
          </div>
        </div>
      </div>
    );
  }

  const catChart = data.by_category
    .filter((c) => c.carrying_amount > 0)
    .sort((a, b) => b.carrying_amount - a.carrying_amount)
    .slice(0, 10)
    .map((c) => ({
      name: getCategoryName(c.category_id),
      shortName: getCategoryName(c.category_id).length > 15 ? getCategoryName(c.category_id).substring(0, 15) + "..." : getCategoryName(c.category_id),
      carrying: Math.round(c.carrying_amount),
      cost: Math.round(c.cost),
    }));

  const pieData = data.by_category
    .filter((c) => c.carrying_amount > 0)
    .sort((a, b) => b.carrying_amount - a.carrying_amount)
    .slice(0, 8)
    .map((c) => ({
      name: getCategoryName(c.category_id),
      value: Math.round(c.carrying_amount),
    }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">Dashboard</h1>
          <p className="text-sm text-[#6B7280] mt-1">
            {activeEntity ? `${activeEntity.name} overview` : "Group consolidated view"}
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Carrying Amount" value={formatCurrency(data.total_carrying_amount)} icon={DollarSign} delay={0} />
        <KpiCard label="Original Cost" value={formatCurrency(data.total_cost)} icon={TrendingUp} delay={100} />
        <KpiCard label="Accumulated Depreciation" value={formatCurrency(data.total_accumulated_depreciation)} icon={TrendingDown} delay={200} />
        <KpiCard label="Monthly Depreciation" value={formatCurrency(data.total_monthly_depreciation)} icon={Calculator} delay={300} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KpiCard label="Total Assets" value={data.asset_count.toLocaleString()} icon={Package} delay={400} />
        <KpiCard label="Categories in Use" value={data.by_category.length} icon={Clock} delay={500} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="stratum-card p-6 animate-fade-in-up" style={{ animationDelay: "200ms" }}>
          <h3 className="font-display text-lg font-semibold text-[#0B1020] mb-4">Carrying Amount by Category</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={catChart} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tickFormatter={(v) => `R${(v / 1000000).toFixed(0)}M`} tick={{ fontSize: 11, fill: "#6B7280" }} />
                <YAxis type="category" dataKey="shortName" width={120} tick={{ fontSize: 11, fill: "#1A1A1A" }} />
                <Tooltip
                  formatter={(v) => formatCurrency(v)}
                  contentStyle={{ borderRadius: 8, border: "1px solid #E8E2D5", fontSize: 12 }}
                />
                <Bar dataKey="carrying" fill="#0B1020" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="stratum-card p-6 animate-fade-in-up" style={{ animationDelay: "300ms" }}>
          <h3 className="font-display text-lg font-semibold text-[#0B1020] mb-4">Value Distribution</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} innerRadius={60}>
                  {pieData.map((_, idx) => (
                    <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E8E2D5", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 mt-2 justify-center">
              {pieData.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex items-center gap-1.5 text-xs text-[#6B7280]">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: CHART_COLORS[idx] }} />
                  <span>{item.name.length > 20 ? item.name.substring(0, 20) + "..." : item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {data.recent_activity.length > 0 && (
        <div className="stratum-card p-6 animate-fade-in-up" style={{ animationDelay: "400ms" }}>
          <h3 className="font-display text-lg font-semibold text-[#0B1020] mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {data.recent_activity.slice(0, 5).map((log) => (
              <div key={log.id} className="flex items-center justify-between py-2 border-b border-[#E8E2D5] last:border-0">
                <div>
                  <span className="text-sm font-medium text-[#1A1A1A] capitalize">{log.action?.toLowerCase()}</span>
                  <span className="text-sm text-[#6B7280] ml-2">{log.entity_type}</span>
                </div>
                <span className="text-xs text-[#6B7280] font-mono tabular-nums">{log.timestamp?.substring(0, 16)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
